import React, { useState, useEffect } from 'react';
import { Typography, Spin, Empty, message, ColorPicker, Button, Tooltip, Input } from 'antd';
import { CopyOutlined, BgColorsOutlined, AimOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import apiClient from '../../../api/client';

const { Text } = Typography;

/**
 * ColorPalettesPopover - Popover content with eyedropper, color picker, and saved palettes
 * Features:
 * - Eyedropper tool for picking colors pixel-by-pixel from screen
 * - General color picker for manual selection
 * - Palette builder to collect picked colors
 * - Saved palettes displayed as horizontal pill layouts
 */
const ColorPalettesPopover = ({ canvasId }) => {
    const [loading, setLoading] = useState(false);
    const [palettes, setPalettes] = useState([]);
    const [selectedColor, setSelectedColor] = useState('#1677ff');
    const [pickedColors, setPickedColors] = useState([]);
    const [paletteName, setPaletteName] = useState('');
    const [savingPalette, setSavingPalette] = useState(false);
    const [isEyedropperSupported] = useState(() => 'EyeDropper' in window);

    // Fetch palettes on mount
    useEffect(() => {
        const fetchPalettes = async () => {
            setLoading(true);
            try {
                // First try canvas-specific, then all
                let response = await apiClient.get('/color-palettes', {
                    params: canvasId ? { canvasId } : {}
                });

                // If no canvas palettes, fetch all
                if (response.data.length === 0 && canvasId) {
                    response = await apiClient.get('/color-palettes');
                }

                setPalettes(response.data);
            } catch (error) {
                console.error('Failed to fetch palettes:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPalettes();
    }, [canvasId]);

    const handleCopyColor = (color) => {
        navigator.clipboard.writeText(color);
        message.success(`Copied ${color}`);
    };

    const handleColorChange = (color, hex) => {
        // Ant Design v6: hex is a string like '#rrggbb' or '#rrggbbaa'
        // Fallback to color.toHexString() if hex is not a proper string
        const hexValue = (typeof hex === 'string' && hex.startsWith('#'))
            ? hex
            : (color?.toHexString ? color.toHexString() : String(color));
        setSelectedColor(hexValue);
    };

    // Eyedropper - pick color from screen
    const handleEyedropper = async () => {
        if (!isEyedropperSupported) {
            message.warning('Eyedropper is not supported in this browser');
            return;
        }

        try {
            const eyeDropper = new window.EyeDropper();
            const result = await eyeDropper.open();
            const color = result.sRGBHex;
            setSelectedColor(color);
            message.success(`Picked ${color}`);
        } catch (error) {
            // User cancelled or error
            if (error.name !== 'AbortError') {
                console.error('Eyedropper error:', error);
            }
        }
    };

    // Add current color to palette builder
    const addColorToPalette = () => {
        if (!pickedColors.includes(selectedColor)) {
            setPickedColors([...pickedColors, selectedColor]);
        }
    };

    // Remove color from palette builder
    const removeColorFromPalette = (colorToRemove) => {
        setPickedColors(pickedColors.filter(c => c !== colorToRemove));
    };

    // Save the built palette
    const savePalette = async () => {
        if (pickedColors.length === 0) {
            message.warning('Add some colors first');
            return;
        }
        if (!paletteName.trim()) {
            message.warning('Enter a palette name');
            return;
        }

        setSavingPalette(true);
        try {
            const payload = {
                name: paletteName.trim(),
                colors: pickedColors.map(c => {
                    // Normalize to 6-char hex (strip alpha if present)
                    const normalized = c.length > 7 ? c.substring(0, 7) : c;
                    return { color: normalized };
                })
            };
            if (canvasId) {
                payload.canvasId = canvasId;
            }
            const response = await apiClient.post('/color-palettes', payload);

            // Add to local state
            setPalettes([response.data, ...palettes]);

            // Clear builder
            setPickedColors([]);
            setPaletteName('');
            message.success('Palette saved!');
        } catch (error) {
            console.error('Failed to save palette:', error);
            message.error('Failed to save palette');
        } finally {
            setSavingPalette(false);
        }
    };

    return (
        <div style={{
            width: 340,
            maxHeight: 560,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Eyedropper & Color Picker Section */}
            <div style={{
                padding: 16,
                borderBottom: '1px solid #30363d'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12
                }}>
                    <AimOutlined style={{ color: '#58a6ff', fontSize: 14 }} />
                    <Text style={{ color: '#e6edf3', fontWeight: 600, fontSize: 13 }}>
                        Pick Colors
                    </Text>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    {/* Eyedropper Button */}
                    <Tooltip title={isEyedropperSupported ? "Pick color from screen" : "Eyedropper not supported"}>
                        <Button
                            icon={<AimOutlined />}
                            onClick={handleEyedropper}
                            disabled={!isEyedropperSupported}
                            style={{
                                background: '#238636',
                                borderColor: '#238636',
                                color: '#fff',
                                fontWeight: 500
                            }}
                        >
                            Eyedropper
                        </Button>
                    </Tooltip>

                    {/* Color Picker */}
                    <ColorPicker
                        value={selectedColor}
                        onChange={handleColorChange}
                        showText
                        size="middle"
                        styles={{
                            popupOverlayInner: {
                                background: '#161b22'
                            }
                        }}
                    />
                </div>

                {/* Selected Color Display */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: '#0d1117',
                    borderRadius: 6,
                    border: '1px solid #30363d'
                }}>
                    <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: selectedColor,
                        border: '2px solid #30363d',
                        flexShrink: 0,
                        boxShadow: `0 0 12px ${selectedColor}40`
                    }} />
                    <Text style={{
                        color: '#e6edf3',
                        fontFamily: 'monospace',
                        fontSize: 14,
                        fontWeight: 600,
                        flex: 1
                    }}>
                        {selectedColor?.toUpperCase()}
                    </Text>
                    <Tooltip title="Copy">
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyColor(selectedColor)}
                            style={{ color: '#8b949e' }}
                        />
                    </Tooltip>
                    <Tooltip title="Add to palette">
                        <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={addColorToPalette}
                            style={{ color: '#58a6ff' }}
                        />
                    </Tooltip>
                </div>
            </div>

            {/* Palette Builder Section */}
            <div style={{
                padding: 16,
                borderBottom: '1px solid #30363d',
                background: '#0d1117'
            }}>
                <Text style={{
                    color: '#8b949e',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    marginBottom: 10
                }}>
                    Build Palette
                </Text>

                {/* Picked Colors Row */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    minHeight: 36,
                    marginBottom: 10
                }}>
                    {pickedColors.length === 0 ? (
                        <Text style={{ color: '#484f58', fontSize: 12, fontStyle: 'italic' }}>
                            Pick colors with the eyedropper and click + to add them here
                        </Text>
                    ) : (
                        pickedColors.map((color, i) => (
                            <Tooltip key={i} title={`${color} - Click to remove`}>
                                <div
                                    onClick={() => removeColorFromPalette(color)}
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 6,
                                        background: color,
                                        border: '2px solid #30363d',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.1)';
                                        e.currentTarget.style.borderColor = '#f85149';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.borderColor = '#30363d';
                                    }}
                                >
                                    <DeleteOutlined style={{
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: 12,
                                        opacity: 0,
                                        transition: 'opacity 0.15s'
                                    }} />
                                </div>
                            </Tooltip>
                        ))
                    )}
                </div>

                {/* Save Palette Row */}
                {pickedColors.length > 0 && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Input
                            placeholder="Palette name..."
                            value={paletteName}
                            onChange={(e) => setPaletteName(e.target.value)}
                            style={{
                                flex: 1,
                                background: '#161b22',
                                border: '1px solid #30363d',
                                borderRadius: 6
                            }}
                            onPressEnter={savePalette}
                        />
                        <Button
                            type="primary"
                            onClick={savePalette}
                            loading={savingPalette}
                            style={{ background: '#238636', borderColor: '#238636' }}
                        >
                            Save
                        </Button>
                    </div>
                )}
            </div>

            {/* Saved Palettes Section */}
            <div style={{
                flex: 1,
                padding: 16,
                overflowY: 'auto'
            }}>
                <Text style={{
                    color: '#8b949e',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    marginBottom: 12
                }}>
                    Saved Palettes
                </Text>

                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: 24
                    }}>
                        <Spin size="small" />
                    </div>
                ) : palettes.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: '#8b949e', fontSize: 12 }}>
                                No palettes saved yet
                            </span>
                        }
                        style={{ margin: '16px 0' }}
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {palettes.map((palette) => (
                            <div key={palette.id}>
                                {/* Palette Name */}
                                <Text style={{
                                    color: '#e6edf3',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    display: 'block',
                                    marginBottom: 8
                                }}>
                                    {palette.name}
                                </Text>

                                {/* Color Pills Row */}
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 6
                                }}>
                                    {palette.colors?.map((item, i) => {
                                        const color = typeof item === 'string' ? item : item.color;
                                        return (
                                            <Tooltip
                                                key={i}
                                                title={`${color.toUpperCase()} - Click to copy`}
                                                placement="top"
                                            >
                                                <div
                                                    onClick={() => handleCopyColor(color)}
                                                    style={{
                                                        width: 36,
                                                        height: 24,
                                                        borderRadius: 12,
                                                        background: color,
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1.15)';
                                                        e.currentTarget.style.boxShadow = `0 2px 8px ${color}60`;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ColorPalettesPopover;
